import { useState, type ComponentProps } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Loader2, Square, RotateCw, MessageSquare } from 'lucide-react'

import { api, useReopenTopic } from '../lib/api'
import type { Topic } from '../lib/apiModels'
import Composer from './Composer'
import MessageList from './MessageList'

type SessionStatus = 'idle' | 'background' | 'running' | 'starting' | 'waiting' | 'released' | 'archived' | 'error'
type Session = { mode: 'chat'; status: SessionStatus; error: string | null; stoppable: boolean }

function parseSession(value: unknown): Session {
  if (typeof value !== 'object' || value === null || !('mode' in value)) throw new Error('Conversation status is unavailable.')
  if (value.mode === 'chat' && 'status' in value && 'error' in value && 'stoppable' in value
    && typeof value.stoppable === 'boolean' && (value.error === null || typeof value.error === 'string')
    && (value.status === 'idle' || value.status === 'background' || value.status === 'running' || value.status === 'starting'
      || value.status === 'waiting' || value.status === 'released' || value.status === 'archived' || value.status === 'error')) {
    return { mode: 'chat', status: value.status, error: value.error, stoppable: value.stoppable }
  }
  throw new Error('Conversation returned an invalid status.')
}

type Props = {
  chatId: string
  topicId: string
  topic: Topic | null
  closed: boolean
  readOnly?: boolean
  autoFocus: boolean
  chatAgent: ComponentProps<typeof Composer>['chatAgent']
}

const STATUS_LABELS: Record<SessionStatus, string> = {
  idle: 'Ready', background: 'Background work running', running: 'Working', starting: 'Resuming conversation', waiting: 'Waiting for your answer',
  released: 'Ready to continue', archived: 'Archived conversation', error: 'Conversation needs attention',
}

export default function CodeConversation({ chatId, topicId, topic, closed, readOnly = false, autoFocus, chatAgent }: Props) {
  const reopen = useReopenTopic(chatId)
  const [action, setAction] = useState<'stop' | 'reconnect' | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const endpoint = `/api/chats/${encodeURIComponent(chatId)}/topics/${encodeURIComponent(topicId)}/code-chat`
  const session = useQuery({
    queryKey: ['code-conversation', chatId, topicId],
    queryFn: async () => parseSession(await api<unknown>(endpoint)),
    refetchInterval: closed ? false : 1500,
    retry: false,
  })
  async function runAction(next: 'stop' | 'reconnect') {
    setAction(next)
    setActionError(null)
    try {
      await api<unknown>(`${endpoint}/${next}`, { method: 'POST' })
      await session.refetch()
    } catch (error) {
      setActionError(error instanceof Error ? error.message : 'The action could not be confirmed.')
    } finally {
      setAction(null)
    }
  }

  const state = session.data ?? null
  const busy = state?.status === 'running' || state?.status === 'starting' || state?.status === 'background'
  return (
    <section className="flex flex-1 min-h-0 min-w-0 flex-col" aria-label="Code conversation">
      <div className="flex min-h-11 items-center justify-between gap-3 border-b border-stone-200/70 px-4 dark:border-neutral-800"
        aria-label="Conversation status">
        <div role="status" className="flex min-w-0 items-center gap-2 text-xs text-stone-500 dark:text-neutral-400">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <MessageSquare className="h-3.5 w-3.5" />}
          <span>{closed ? STATUS_LABELS.archived : state ? STATUS_LABELS[state.status] : 'Opening conversation…'}</span>
        </div>
        {!closed && state?.stoppable && <button type="button" aria-label="Stop Code turn" disabled={action !== null}
          onClick={() => void runAction('stop')}
          className="inline-flex min-h-11 items-center gap-2 px-3 text-sm font-medium text-red-600 disabled:opacity-50 dark:text-red-400">
          <Square className="h-3 w-3" />{action === 'stop' ? 'Stopping…' : 'Stop'}
        </button>}
      </div>
      {(session.isError || state?.error || actionError) && <div role="alert"
        className="flex items-center justify-between gap-3 border-b border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
        <span>{actionError || state?.error || 'Could not connect. Your saved history is available below.'}</span>
        {!closed && <button type="button" disabled={action !== null} onClick={() => void runAction('reconnect')}
          className="inline-flex min-h-11 shrink-0 items-center gap-2 px-3 font-medium">
          <RotateCw className="h-3.5 w-3.5" />Reconnect
        </button>}
      </div>}
      {closed && !readOnly && <div className="flex flex-wrap items-center justify-between gap-2 border-b border-blue-200 bg-blue-50 px-4 py-2 text-sm text-blue-900 dark:border-blue-900 dark:bg-blue-950/30 dark:text-blue-200">
        <span>Send a message to reopen this conversation.</span>
        <button type="button" disabled={reopen.isPending}
          onClick={() => {
            setActionError(null)
            reopen.mutate({ topic_id: topicId, reopenTodoist: false }, {
              onSuccess: () => { void session.refetch() },
              onError: (error) => setActionError(error instanceof Error ? error.message : 'Could not reopen this conversation.'),
            })
          }}
          className="inline-flex min-h-11 items-center gap-2 rounded-lg bg-blue-600 px-3 font-medium text-white disabled:opacity-50">
          <RotateCw className="h-3.5 w-3.5" />{reopen.isPending ? 'Reopening…' : 'Reopen conversation'}
        </button>
      </div>}
      <div className="flex-1 min-h-0 overflow-hidden chat-pattern-bg">
        <MessageList chatId={chatId} topicId={topicId} chatType="auto-spawn" currentTopicTitle={topic?.title ?? null} />
      </div>
      {!readOnly && <Composer chatId={chatId} topicId={topicId} autoFocus={autoFocus}
        chatAgent={chatAgent} chatType="auto-spawn" enableClaudeEffort={chatAgent === 'claude'}
        lastTurnRuntime={topic?.last_turn_runtime} />}
    </section>
  )
}
